import { Box, Typography, useTheme } from "@material-ui/core";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useStyles from "../../containers/data/forms/forms-styles";
import { EMPTY_VALUE_PLACEHOLDER } from "../../models/common/data";
import { CADataPoints } from "../../models/flexible/ca-data-points";
import { FlexContext } from "../../models/flexible/flex-context";
import { getPreparationsWithDistinguishBrands as getPreparations } from "../../utils/agreement-utils";
import { indicationDisplay } from "../../utils/base-agreement-formatters";
import { distinctBy } from "../../utils/distinct-by";

export default function IncludedCoverageApprovalReadOnlyFields(props: { context: FlexContext }) {
	const { t } = useTranslation();
	const styles = useStyles();
	const { palette } = useTheme();
	const caseIds = props.context.cases.map((cs) => cs.id);

	if (!props.context.startingPoint) {
		return <></>;
	}

	const financialDataPoints = props.context.startingPoint.data as unknown as CADataPoints;

	function oneLegalEntityOrPatientHasValues(financialDataPoints: CADataPoints) {
		return financialDataPoints?.legalEntity?.name || financialDataPoints?.initials || financialDataPoints?.yearOfBirth;
	}

	return (
		<Box mt={3}>
			<Box display="flex" mb={3} mx={3}>
				<Box className={styles.colMedium} pl={1.5} pr={5}>
					<Typography variant="caption" color="textSecondary" style={{ color: palette.enabledHeader.main }}>
						{t("iin")}
					</Typography>
					<Typography>{financialDataPoints.patientId}</Typography>
				</Box>
				<Box className={styles.colMedium} pl={1.5} pr={5}>
					<Typography variant="caption" style={{ color: palette.enabledHeader.main }}>
						{t("case-id")}
					</Typography>
					<Box display="flex" flexWrap="wrap">
						{caseIds.map((caseId, i) => (
							<Fragment key={i}>
								<Link to={`/cases/case?id=${caseId}`}>{caseId}</Link>
								{i < caseIds.length - 1 && <Box mr={1}>, </Box>}
							</Fragment>
						))}
					</Box>
				</Box>
				<Box className={styles.colMedium} pl={1.5} pr={5}>
					<Typography variant="caption" color="textSecondary" style={{ color: palette.enabledHeader.main }}>
						{t("cr-id")}
					</Typography>
					<Typography style={{ color: palette.text.disabled }}>{props.context.startingPoint.id}</Typography>
				</Box>
			</Box>
			{oneLegalEntityOrPatientHasValues(financialDataPoints) && (
				<Box display="flex" mb={3} mx={3}>
					<Box className={styles.colMedium} pl={1.5} pr={5}>
						<Typography variant="caption" style={{ color: palette.enabledHeader.main }}>
							{t("legal-entity")}
						</Typography>
						<Typography>{financialDataPoints?.legalEntity?.name || EMPTY_VALUE_PLACEHOLDER}</Typography>
					</Box>
					<Box className={styles.colMedium} pl={1.5} pr={5}>
						<Typography variant="caption" style={{ color: palette.enabledHeader.main }}>
							{t("patient-initials")}
						</Typography>
						<Typography>{financialDataPoints?.initials || EMPTY_VALUE_PLACEHOLDER}</Typography>
					</Box>
					<Box className={styles.colMedium} pl={1.5} pr={5}>
						<Typography variant="caption" style={{ color: palette.enabledHeader.main }}>
							{t("yob")}
						</Typography>
						<Typography>{financialDataPoints?.yearOfBirth || EMPTY_VALUE_PLACEHOLDER}</Typography>
					</Box>
				</Box>
			)}
			<Box display="flex" mb={3} mx={3}>
				<Box className={styles.colMedium} pl={1.5} pr={5}>
					<Typography variant="caption" style={{ color: palette.enabledHeader.main }}>
						{getPreparations(financialDataPoints).length && getPreparations(financialDataPoints).length === 1
							? t("treatment")
							: t("treatments")}
					</Typography>
					{getPreparations(financialDataPoints).length &&
						distinctBy(
							getPreparations(financialDataPoints),
							(preparationDataPoints) => preparationDataPoints.brandName.Name
						).map((preparation, index) => (
							<Typography key={index}>{`${preparation?.brandName.Name} (${preparation?.genericName.Name})`}</Typography>
						))}
				</Box>
				<Box className={styles.colMedium} pl={1.5} pr={5}>
					<Typography variant="caption" style={{ color: palette.enabledHeader.main }}>
						{t("disease")}
					</Typography>
					<Typography>{financialDataPoints?.disease?.Name}</Typography>
				</Box>
				<Box className={styles.colMedium} pl={1.5} pr={5}>
					<Typography variant="caption" style={{ color: palette.enabledHeader.main }}>
						{t("indication")}
					</Typography>
					<Typography>{indicationDisplay(financialDataPoints?.indication, t)}</Typography>
				</Box>
			</Box>
		</Box>
	);
}
